import { useId, type HTMLAttributes, type KeyboardEvent, type ReactNode } from "react";
import { EmptyState } from "./basics";

/* One component for both layouts: below 720px every row is a stacked card, 720px+ a table
   (styles.css). Cells get their card role the way the prototype's decorateTables() did: the first
   column is the title, a money/hours column is the key value on the right, other columns are
   secondary text (labelled when they're dates, owners, rates…), controls get their own row. */

export type CellRole = "pri" | "key" | "sec" | "wide" | "hide" | "ctl";

export interface Column<T> {
  header: string;
  /** What the header shows when it's more than text (e.g. a button); `header` still labels phone cards. */
  headerNode?: ReactNode;
  cell: (row: T) => ReactNode;
  /** Right-align (numbers, money). */
  align?: "right";
  /** Card role on phones; inferred when omitted. */
  role?: CellRole;
  /** Show the header as a label before the value on phones; inferred when omitted. */
  label?: boolean;
  className?: string;
  /** Leave the column out (e.g. money the user may not see); `foot` cells follow the shown ones. */
  hidden?: boolean;
}

const KEY_RE = /total|amount|value|budget|pipeline|projected|commission|^hours$/i;
const LAB_RE =
  /date|created|sent|valid|due|issued|deleted|last|expires|connected|next|updated|close|purges|contents|scopes|token|trigger|^when$|owner|assignee|phase|qty|disc|unit|rate/i;

function keyIndex<T>(cols: Column<T>[]): number {
  const explicit = cols.findIndex((c) => c.role === "key");
  if (explicit >= 0) return explicit;
  const k = cols.findIndex((c, i) => i > 0 && c.align === "right" && KEY_RE.test(c.header) && !c.role);
  if (k >= 0) return k;
  for (let i = cols.length - 1; i > 0; i--) if (cols[i]?.align === "right" && !cols[i]?.role) return i;
  return -1;
}

function isBlank(v: ReactNode) {
  return v === null || v === undefined || v === false || v === "" || v === "—";
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  empty?: ReactNode;
  foot?: ReactNode[];
  className?: string;
  /** Label for the table (screen readers). */
  label?: string;
  /** Extra classes for a row (e.g. a struck-out finished task). */
  rowClassName?: (row: T) => string | false | null | undefined;
  /** Extra attributes for a row (e.g. drop targets for dragging lines). */
  rowProps?: (row: T) => HTMLAttributes<HTMLTableRowElement>;
  /** A row that is a group heading rather than data (UX-213): one `th scope="rowgroup"` spanning the
      table. Return null for ordinary rows. `level` 2 is a sub-group. */
  sectionRow?: (row: T) => { id?: string; level?: 1 | 2; content: ReactNode } | null;
}

export function DataTable<T>({
  columns: allColumns,
  rows,
  rowKey,
  onRowClick,
  empty = "Nothing here yet.",
  foot,
  className,
  label,
  rowClassName,
  rowProps,
  sectionRow,
}: DataTableProps<T>) {
  const uid = useId();
  if (!rows.length) return <EmptyState>{empty}</EmptyState>;
  const columns = allColumns.filter((c) => !c.hidden);
  const key = keyIndex(columns);
  const headId = (i: number) => `${uid}-grp-${i}`;
  const renderRow = (row: T, headId: (i: number) => string, index: number) => {
    const section = sectionRow?.(row);
    if (section)
      return (
        <tr key={rowKey(row)} className={`grp grp-${section.level ?? 1}`}>
          <th
            scope={(section.level ?? 1) === 1 ? "rowgroup" : undefined}
            colSpan={columns.length}
            id={section.id ?? headId(index)}
          >
            {section.content}
          </th>
        </tr>
      );
    let ctlRow = false;
    const cells = columns.map((c, i) => {
      const v = c.cell(row);
      let role: CellRole;
      if (c.role) role = c.role;
      else if (i === 0) role = "pri";
      else if (i === key) role = "key";
      else if (isBlank(v)) role = "hide";
      else role = "sec";
      if (role === "ctl") ctlRow = true;
      const lab = role === "sec" && (c.label ?? (c.align === "right" || LAB_RE.test(c.header)));
      const cls = [`c-${role}`, lab && "c-lab", c.align === "right" && "r", c.className]
        .filter(Boolean)
        .join(" ");
      return (
        <td key={i} className={cls} data-label={c.header || undefined}>
          {isBlank(v) && role !== "hide" ? "—" : v}
        </td>
      );
    });
    const click = onRowClick
      ? {
          "data-href": "",
          tabIndex: 0,
          onClick: (e: { target: EventTarget }) => {
            if ((e.target as HTMLElement).closest("a,button,input,select,textarea,label")) return;
            onRowClick(row);
          },
          onKeyDown: (e: KeyboardEvent) => {
            if (e.key === "Enter" && e.target === e.currentTarget) onRowClick(row);
          },
        }
      : {};
    return (
      <tr
        key={rowKey(row)}
        className={[ctlRow && "has-ctl", rowClassName?.(row)].filter(Boolean).join(" ") || undefined}
        {...rowProps?.(row)}
        {...click}
      >
        {cells}
      </tr>
    );
  };
  // One tbody per top-level group (a section row of level 1 starts the next), labelled by its heading.
  const chunks: { heading?: string; rows: { row: T; index: number }[] }[] = [];
  rows.forEach((row, index) => {
    const section = sectionRow?.(row);
    if (section && (section.level ?? 1) === 1)
      chunks.push({ heading: section.id ?? headId(index), rows: [] });
    else if (!chunks.length) chunks.push({ rows: [] });
    chunks[chunks.length - 1]!.rows.push({ row, index });
  });
  return (
    <div className="tbl-wrap">
      <table className={className} aria-label={label}>
        <thead>
          <tr>
            {columns.map((c, i) => (
              <th key={i} className={c.align === "right" ? "r" : undefined}>
                {c.headerNode ?? c.header}
              </th>
            ))}
          </tr>
        </thead>
        {chunks.map((chunk, ci) => (
          <tbody key={ci} aria-labelledby={chunk.heading}>
            {chunk.rows.map((r) => renderRow(r.row, headId, r.index))}
          </tbody>
        ))}
        {foot && (
          <tfoot>
            <tr>
              {foot.map((f, i) => (
                <td
                  key={i}
                  className={[
                    i === 0 ? "c-pri" : i === foot.length - 1 ? "c-key" : "c-hide",
                    columns[i]?.align === "right" && "r",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  {f}
                </td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}
